import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Eye, Trash2 } from 'lucide-react';
import { care, fail, can, openFile } from './careApi';
import { Btn, Select, Table } from './careUi';
import { ask } from './AskDialog';

export default function DocumentsTab({ rid, docTypes, required, onChanged }) {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState(docTypes[0]);
  const [busy, setBusy] = useState(false);
  const load = () => care.get(`/residents/${rid}/documents`).then((r) => setRows(r.data)).catch(fail);
  useEffect(() => { load(); }, [rid]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error('File is larger than 10 MB');
    const fd = new FormData();
    fd.append('doc_type', type); fd.append('file', file);
    setBusy(true);
    try { await care.post(`/residents/${rid}/documents`, fd); toast.success('Document uploaded'); load(); onChanged(); }
    catch (err) { fail(err); } finally { setBusy(false); }
  };
  const remove = async (d) => {
    if (!await ask({ title: 'Remove document', message: `Remove ${d.filename}? It stays in the audit trail.`, danger: true, confirm: 'Remove' })) return;
    try { await care.delete(`/documents/${d.id}`); load(); onChanged(); } catch (err) { fail(err); }
  };
  const have = new Set(rows.map((r) => r.doc_type));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {required.map((d) => (
          <span key={d} className={`text-xs px-2.5 py-1 rounded-full ${have.has(d) ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
            {have.has(d) ? '✓' : '•'} {d}
          </span>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <Select label="Document type" value={type} onChange={(e) => setType(e.target.value)} options={docTypes} testid="doc-type" className="sm:w-80" />
        <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-teal-700 hover:bg-teal-800 text-white cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload className="w-4 h-4" />{busy ? 'Uploading…' : 'Upload file'}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={upload} data-testid="doc-upload-input" />
        </label>
      </div>
      <Table testid="docs-table" rows={rows} cols={[
        { k: 'doc_type', h: 'Type' }, { k: 'filename', h: 'File' }, { k: 'uploaded_by', h: 'Uploaded by' },
        { k: 'created_at', h: 'Date', r: (d) => d.created_at.slice(0, 10) },
        { k: 'a', h: '', right: true, r: (d) => (
          <div className="flex justify-end gap-1">
            <Btn variant="ghost" className="!px-2 !py-1" onClick={() => openFile(`/documents/${d.id}/download`)} data-testid={`doc-view-${d.id}`}><Eye className="w-4 h-4" /></Btn>
            {can('admin') && <Btn variant="ghost" className="!px-2 !py-1" onClick={() => remove(d)} data-testid={`doc-remove-${d.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Btn>}
          </div>) },
      ]} />
    </div>
  );
}
